import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api';
import { debugAuth } from '../utils/authDebug';
import { setUserSoundEnabled, setEscalationSound } from '../utils/soundPrefs';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const clearSession = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
      localStorage.removeItem('wati_operator_email');
      delete api.defaults.headers.common['x-access-token'];
      setUser(null);
      navigate('/login');
    };

    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp < currentTime) {
          // Token expired
          debugAuth.log('Token expired on load', { exp: decoded.exp, currentTime });
          clearSession();
        } else {
          debugAuth.log('Token valid on load', { exp: decoded.exp, currentTime });
          // Set user from token
          setUser({
            id: decoded.user_id,
            username: localStorage.getItem('username'),
            role: localStorage.getItem('role'),
            wati_operator_email: localStorage.getItem('wati_operator_email') || '',
          });
          
          // Set authorization header for API calls
          api.defaults.headers.common['x-access-token'] = token;

          // Sincronizar preferencias que un admin puede haber cambiado
          // (ej: sound_enabled) sin esperar al próximo login. Fire-and-forget.
          api.get('/api/me')
            .then((r) => {
              if (typeof r.data?.sound_enabled === 'boolean') setUserSoundEnabled(r.data.sound_enabled);
              if (r.data?.escalation_sound) setEscalationSound(r.data.escalation_sound);
            })
            .catch(() => {});
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        clearSession();
      }
    }
    setLoading(false);
    // Init corre una sola vez al mount; navigate puede cambiar de referencia
    // y dispararía setUser otra vez → reconnect del socket en cada navegación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 'token' vive en localStorage, compartido por TODAS las pestañas del
    // mismo navegador/perfil. Si dos operadores usan la misma compu (turnos,
    // mostrador), el login/logout de uno en otra pestaña pisa la sesión de
    // esta sin avisar — el próximo request de esta pestaña falla con 401 en
    // silencio (redirect duro sin explicación). Avisamos apenas cambia.
    const handleStorageChange = (e) => {
      if (e.key !== 'token' || e.newValue === e.oldValue) return;
      if (!e.newValue) {
        alert('Tu sesión se cerró desde otra pestaña de este navegador. Recargá la página para continuar.');
      } else {
        alert('Otro usuario inició sesión en este navegador (misma pestaña/perfil). Recargá la página — vas a tener que volver a entrar con tu cuenta.');
      }
      window.location.reload();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await api.post('/api/login', credentials);
      const { token, username, role, wati_operator_email, avatar_url, sound_enabled, escalation_sound } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('username', username);
      localStorage.setItem('role', role);
      localStorage.setItem('wati_operator_email', wati_operator_email || '');
      localStorage.setItem('user_avatar', avatar_url || '');
      setUserSoundEnabled(sound_enabled !== false); // espejo del flag por-usuario (admin)
      setEscalationSound(escalation_sound); // tono de escalaciones elegido por el usuario
      
      // Set authorization header for future API calls
      api.defaults.headers.common['x-access-token'] = token;
      
      setUser({
        id: jwtDecode(token).user_id,
        username,
        role,
        wati_operator_email: wati_operator_email || ''
      });
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('wati_operator_email');
    delete api.defaults.headers.common['x-access-token'];
    setUser(null);
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
